import store from '@/store';
import imageService from '../imageService';
import apiService from '../apiService';
import workflowService from '../workflowService';
import bundleOptionService from './bundleOptionService';
import { ensureContentQuantity } from './koreanLabelFields';
import { setStepCount } from './workProgress';
import { logServiceError, logAIRequestError, ERROR_TYPES } from './errorLoggingService';
import { LABEL_EVIDENCE_RULES } from './koreanLabelFields';

/**
 * 한글 표시사항 편집 화면의 기본 항목 (정본 — 화면 초기값·초기화·이미지 항목명 폴백이 모두 이것을 쓴다)
 */
export const DEFAULT_KOREAN_LABEL_ITEMS = [
  { id: 'productName', label: '제품명', required: true },
  { id: 'importer', label: '수입 및 판매원', required: false },
  { id: 'manufacturer', label: '제조원', required: false },
  { id: 'madein', label: '제조국', required: false },
  { id: 'content', label: '내용량', required: false },
  { id: 'material', label: '원료명 및 성분명(재질)', required: false },
  { id: 'type', label: '상품 유형', required: false },
  { id: 'caution', label: '사용 시 주의사항', required: false },
  { id: 'standard', label: '사용 기준', required: false }
];

// 자동 생성 라벨이 써 온 항목명 (문구를 바꾸면 기존 라벨 이미지가 달라지므로 그대로 둔다)
const LABEL_NAME_MAP = {
  'productName': '제품명',
  'manufacturer': '제조원',
  'importer': '수입 및 판매원',
  'madein': '제조국',
  'content': '내용량',
  'material': '원료명 및 성분명(재질)',
  'type': '상품 유형',
  'caution': '사용 시 주의사항',
  'standard': '사용 기준'
};

/**
 * 이미지에 찍을 항목명 표 — 사용자 구조(id→label) 우선, 없으면 고정표, 고정표에 없는 키는 화면 기본 항목명
 * @param {Array<{id: string, label: string}>} [labelStructure]
 * @returns {Object}
 */
export function buildLabelNameMap(labelStructure) {
  const map = {};
  DEFAULT_KOREAN_LABEL_ITEMS.forEach(item => { map[item.id] = item.label; });
  Object.assign(map, LABEL_NAME_MAP);
  if (Array.isArray(labelStructure)) {
    labelStructure.forEach(item => {
      if (item && item.id && item.label) map[item.id] = item.label;
    });
  }
  return map;
}

/**
 * 한글 표시사항 처리를 위한 서비스
 */
const koreanLabelService = {
  /**
   * 한글 표시사항 작업 후 상품 및 coupangDoc 업데이트 (부분 업데이트 방식)
   * @param {Object} product - 상품 객체
   * @param {Object} labelData - 한글 표시사항 작업 결과 데이터
   * @returns {Promise<boolean>} 업데이트 성공 여부
   */
  async updateProductWithCoupangDocPartial(product, labelData) {
    try {
      if (!product || !product._id) {
        throw new Error('유효하지 않은 상품 정보입니다');
      }

      // 점 표기법을 사용하여 업데이트할 필드 지정
      const updateFields = {
        'koreanLabelStatus': 'completed',
        'updatedAt': Date.now()
      };

      // 한글 표시사항 정보 업데이트
      updateFields['koreanLabels'] = {
        ...(product.koreanLabels || {}),
        content: labelData.content || '',
        material: labelData.material || '',
        type: labelData.type || ''
      };

      // 한글 표시사항 이미지 URL 설정
      if (labelData.imageUrl) {
        updateFields['koreanLabelImage'] = labelData.imageUrl;
      }

      // 옵션 정보 업데이트
      if (product.options && product.options.length > 0) {
        product.options.forEach((option, optionIndex) => {
          updateFields[`options.${optionIndex}.koreanLabelStatus`] = 'completed';
        });
      }

      // coupangDoc 업데이트 (있는 경우)
      if (product.coupangDoc && product.coupangDoc.jsonDocument) {
        // 모든 jsonDocument 항목에 한글 표시사항 정보 적용
        product.coupangDoc.jsonDocument.forEach((docItem, docIndex) => {
          if (docItem.imagePage && docItem.imagePage.images) {
            // labelImages 배열이 없으면 생성
            if (!docItem.imagePage.images.labelImages) {
              // 메모리 상에서 배열 생성
              docItem.imagePage.images.labelImages = [];
              // 점 표기법으로 빈 배열 설정
              updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images.labelImages`] = [];
            }

            // 한글 표시사항 라벨 이미지 업데이트
            const labelImageIndex = docItem.imagePage.images.labelImages.findIndex(
              img => img.labelImageType === '제품 한글 표시사항 라벨 또는 도안 이미지'
            );

            if (labelData.imageUrl) {
              if (labelImageIndex !== -1) {
                // 기존 이미지 업데이트
                updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images.labelImages.${labelImageIndex}.labelImageFiles`] =
                  labelData.imageUrl;
              } else {
                // 새 이미지 추가 (배열에 요소 추가는 MongoDB 부분 업데이트에서 지원하지 않을 수 있음)
                // 전체 배열을 업데이트하는 방식으로 처리
                const updatedLabelImages = [...docItem.imagePage.images.labelImages, {
                  labelImageType: '제품 한글 표시사항 라벨 또는 도안 이미지',
                  labelImageFiles: labelData.imageUrl
                }];

                updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images.labelImages`] = updatedLabelImages;
              }
            }
          }
        });
      }

      // 서버에 부분 업데이트 요청
      await apiService.updateRocketProductPartial(product._id, updateFields);

      // store 상태 부분 업데이트
      store.commit('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });

      return true;
    } catch (error) {
      console.error('한글 표시사항 정보로 상품 업데이트 중 오류:', error);
      throw error;
    }
  },

  /**
   * 한글 표시사항 작업 후 상품 및 coupangDoc 업데이트 (이전 방식 - 호환성 유지)
   * @param {Object} product - 상품 객체
   * @param {Object} labelData - 한글 표시사항 작업 결과 데이터
   * @returns {Promise<Object>} 업데이트된 상품 객체
   * @deprecated 새로운 updateProductWithCoupangDocPartial 메서드를 사용하세요
   */
  async updateProductWithCoupangDoc(product, labelData) {
    console.warn('updateProductWithCoupangDoc는 더 이상 사용되지 않습니다. updateProductWithCoupangDocPartial을 사용하세요.');
    return this.updateProductWithCoupangDocPartial(product, labelData);
  },

  /**
   * 한글 표시사항 작업
   * @param {Object} product
   * @param {Object} labels
   * @returns
   */
  // koreanLabelService.js의 processKoreanLabels 함수 수정
  async processKoreanLabels(product) {
    try {
      // 상품 유효성 확인
      if (!product || !product.registrationId) {
        throw new Error('유효하지 않은 상품 정보입니다.');
      }

      // 상태 업데이트: processing
      const updatedProduct = JSON.parse(JSON.stringify(product));
      updatedProduct.koreanLabelStatus = 'processing';

      // 옵션 상태 업데이트
      if (updatedProduct.options && updatedProduct.options.length > 0) {
        updatedProduct.options.forEach(option => {
          option.koreanLabelStatus = 'processing';
        });
      }

      // 로딩 상태 설정
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: 'AI로 한글 표시사항 생성 중...'
      });

      // 기본 라벨 정보 구성 (초기 상태에서 사용)
      const initialLabels = {
        productName: product.productName || product.productNameTrans || '',
        manufacturer: product.manufacturer || store.state.settings[0]?.defaultSettings?.manufacturer || '',
        importer: store.state.settings[0]?.defaultSettings?.importer || '',
        madein: '중국',
        caution: '용도 외에 사용금지. 파손및화기주의',
        standard: '14세이상'
      };

      // 최소한의 기본 정보로 이미지 생성
      const imageBase64 = await this.generateLabelImage({
        ...initialLabels,
        content: '내용량 정보 생성 중...',
        material: '재질 정보 생성 중...',
        type: '상품 유형 정보 생성 중...'
      });

      // 파일명 생성 및 이미지 업로드 (filename 변수를 uploadLabelImage에 전달)
      const filename = `korean_label_${product.registrationId}_${Date.now()}.png`;
      const imageResult = await this.uploadLabelImage(product, imageBase64, filename);
      var imageUrl = imageResult.imageUrl
      // 상태 업데이트: processing 유지, 이미지만 추가
      updatedProduct.koreanLabelImage = imageUrl;

      // 부분 업데이트로 변경
      await apiService.updateRocketProductPartial(product._id, {
        'koreanLabelStatus': 'processing',
        'koreanLabelImage': imageUrl,
        'updatedAt': Date.now()
      });

      // store 상태 부분 업데이트
      store.commit('updateProductPartial', {
        productId: product._id,
        updateFields: {
          'koreanLabelStatus': 'processing',
          'koreanLabelImage': imageUrl,
          'updatedAt': Date.now()
        }
      });

      // 옵션별 한글 표시사항 배치 생성
      try {
        const batchResult = await this.generateAllOptionLabels(product);

        if (batchResult.success) {
          console.log('✅ 옵션별 한글 표시사항 배치 생성 성공');

          // 옵션별로 개별 이미지 생성 및 적용
          await this.applyBatchLabelResults(product, batchResult.results);

        } else {
          console.warn('⚠️ 옵션별 한글 표시사항 배치 생성 실패, 기존 방식으로 폴백');

          // 기존 방식으로 폴백 (모든 옵션에 동일한 한글 표시사항 적용)
          await this.processFallbackLabels(product);
        }

        // 작업 완료 상태 업데이트
        store.commit('ADD_COMPLETED_TASK', 'koreanLabelStatus');
        console.log('koreanLabelStatus 작업 완료 상태 업데이트됨');

        return true;
      } catch (error) {
        console.error('한글 표시사항 처리 실패:', error);
        logAIRequestError('koreanLabelService', 'processKoreanLabels', error, {
          fallbackAction: '폴백 라벨 처리',
          productId: product?.registrationId,
          additional: { optionCount: product?.options?.length }
        });

        // 오류 발생 시 기존 방식으로 폴백
        await this.processFallbackLabels(product);

        // 작업 완료 상태 업데이트 (오류 시에도)
        store.commit('ADD_COMPLETED_TASK', 'koreanLabelStatus');
        console.log('koreanLabelStatus 작업 완료 상태 업데이트됨 (오류 처리)');

        return true;
      }
    } catch (error) {
      console.error('한글 표시사항 처리 중 오류:', error);
      logServiceError('koreanLabelService', 'processKoreanLabels', error, {
        errorType: ERROR_TYPES.UNKNOWN,
        productId: product?.registrationId
      });
      store.dispatch('setProcessing', { isProcessing: false });
      // false 로 삼키면 작업개시가 실패를 못 봐 'processing' 에 멈춘다(10-20 윈 ourworks DDC5C9 · 그리기 3분 한도) — 던져서 'error' + 까닭
      throw error;
    }
  },
  async handleResult(product, result) {
    try {
      if (!product || !result) return false;

      console.log('한글 표시사항 결과 처리 시작:', result);
      console.log('🔍 [DEBUG] result 타입:', typeof result);
      console.log('🔍 [DEBUG] result 구조:', JSON.stringify(result, null, 2));

      // 결과 파싱 시도
      let aiLabels;

      // batchResults 구조 처리
      if (result.batchResults && Array.isArray(result.batchResults) && result.batchResults.length > 0) {
        console.log('🔍 [DEBUG] batchResults 구조 감지');
        const batchResult = result.batchResults[0];
        console.log('🔍 [DEBUG] 첫 번째 batchResult:', JSON.stringify(batchResult, null, 2));

        if (batchResult.result) {
          console.log('🔍 [DEBUG] batchResult.result 사용');
          aiLabels = batchResult.result;
        } else if (batchResult.processedText) {
          console.log('🔍 [DEBUG] batchResult.processedText 파싱 시도');
          try {
            // JSON 블록 제거 후 파싱
            let jsonText = batchResult.processedText;
            jsonText = jsonText.replace(/```json\n|\n```/g, '');
            jsonText = jsonText.replace(/```\n|\n```/g, '');
            jsonText = jsonText.trim();
            console.log('🔍 [DEBUG] 정제된 JSON 텍스트:', jsonText);
            aiLabels = JSON.parse(jsonText);
          } catch (parseError) {
            console.error('🔍 [DEBUG] processedText 파싱 오류:', parseError);
            console.log('🔍 [DEBUG] 원본 processedText:', batchResult.processedText);
            return false;
          }
        } else {
          console.error('🔍 [DEBUG] batchResult에 result나 processedText가 없음');
          return false;
        }
      } else if (typeof result === 'string') {
        console.log('🔍 [DEBUG] 문자열 결과 파싱 시도');
        try {
          aiLabels = JSON.parse(result);
        } catch (e) {
          console.error('한글 표시사항 데이터 파싱 오류:', e);
          return false;
        }
      } else {
        console.log('🔍 [DEBUG] 직접 객체 사용');
        aiLabels = result;
      }

      console.log('🔍 [DEBUG] 최종 파싱된 aiLabels:', JSON.stringify(aiLabels, null, 2));

      // 결과 데이터 확인
      if (!aiLabels) {
        console.error('AI 라벨 데이터가 없습니다.');
        return false;
      }

      // 상품 정보 업데이트 (깊은 복사)
      const updatedProduct = JSON.parse(JSON.stringify(product));

      console.log('AI 결과 값:', aiLabels);
      console.log('기존 koreanLabels:', product.koreanLabels);
      console.log('업데이트된 koreanLabels:', updatedProduct.koreanLabels);
      console.log('koreanLabelImage:', updatedProduct.koreanLabelImage);

      // 기존 koreanLabels 유지하면서 AI 결과 추가
      // 견적서에 저장한 고시정보(첫 옵션 기준 — 이 경로는 상품 레벨 한 장)를 AI 값보다 먼저 쓴다
      const labelValues = this.applySavedNotices({
        content: aiLabels.content || '',
        material: aiLabels.material || '',
        type: aiLabels.type || ''
      }, product, 0, product.options?.[0]?.bundleQuantity);
      updatedProduct.koreanLabels = {
        ...(updatedProduct.koreanLabels || {}),
        ...labelValues,
        // 내용량엔 개수·양 단위가 있어야 한다(치수만 오면 첫 옵션 묶음 수량을 덧붙인다)
        content: ensureContentQuantity(labelValues.content, updatedProduct.options?.[0]?.bundleQuantity)
      };

      // 상태 업데이트: completed
      updatedProduct.koreanLabelStatus = 'completed';

      // 기존 이미지가 없으면 새로 생성
      if (!updatedProduct.koreanLabelImage) {
        // 제품명 준비 — 묶음 표기는 옵션 단위로 판정한다 (labelProductNameFor 주석 참고).
        // 이 이미지는 상품 레벨 한 장이라 첫 옵션을 기준으로 삼는다.
        // applyBatchLabelResults 도 상품 레벨 이미지를 첫 옵션 것으로 맞추므로 같은 기준이다.
        const labelProductName = this.labelProductNameFor(updatedProduct, updatedProduct.options?.[0]);

        // 라벨 정보 구성
        const allLabels = {
          productName: labelProductName,
          manufacturer: updatedProduct.manufacturer || store.state.settings[0]?.defaultSettings?.manufacturer || '',
          importer: store.state.settings[0]?.defaultSettings?.importer || '',
          madein: '중국',
          content: updatedProduct.koreanLabels.content || '',
          material: updatedProduct.koreanLabels.material || '',
          type: updatedProduct.koreanLabels.type || '',
          caution: updatedProduct.koreanLabels?.caution || '용도 외에 사용금지. 파손및화기주의',
          standard: updatedProduct.koreanLabels?.standard || '14세이상'
        };

        // 라벨 이름 매핑
        const labelNameMap = buildLabelNameMap();

        // HTML에서 이미지 생성
        const imageBase64 = await imageService.generateHtmlToImage({
          labels: allLabels,
          labelNameMap
        });

        // S3에 업로드
        const filename = `korean_label_${updatedProduct.registrationId}_${Date.now()}.png`;
        var saveResult = await imageService.uploadImageToS3(imageBase64, filename, 'koreanLabel');
        updatedProduct.koreanLabelImage = saveResult.imageUrl;
      }

      // 옵션 상태 업데이트
      if (updatedProduct.options && updatedProduct.options.length > 0) {
        updatedProduct.options.forEach(option => {
          option.koreanLabelStatus = 'completed';
        });
      }

      // 옵션별 coupangDoc 업데이트는 updateProductWithCoupangDocPartial에서 처리됨
      // 해당 함수에서 상품 레벨의 coupangDoc을 부분 업데이트 방식으로 업데이트함

      // 로딩 상태 해제
      store.dispatch('setProcessing', { isProcessing: false });

      // 라벨 데이터 구성
      const labelData = {
        content: updatedProduct.koreanLabels.content || '',
        material: updatedProduct.koreanLabels.material || '',
        type: updatedProduct.koreanLabels.type || '',
        imageUrl: updatedProduct.koreanLabelImage
      };

      // 서비스에서 직접 부분 업데이트 (명시적으로 koreanLabelService 메서드 호출)
      await koreanLabelService.updateProductWithCoupangDocPartial(updatedProduct, labelData);
      console.log('한글 표시사항 정보로 상품 업데이트 완료');

      console.log('한글 표시사항 업데이트 완료:', updatedProduct.koreanLabels);
      return true;
    } catch (error) {
      console.error('한글 표시사항 결과 처리 중 오류:', error);
      store.dispatch('setProcessing', { isProcessing: false });
      return false;
    }
  },
  /**
   * AI로 라벨값 가져오기
   * @param {Object} product
   * @returns {labels,boolean}
   */
  async getAiLabelValues(product) {
    try {
      // AI 프롬프트 생성
      const prompt = this.promptForKoreanLabels(product);

      // 확장 프로그램에 요청
      const requestId = await this.requestExtension({
        type: 'KOREAN_LABELS',
        productId: product.registrationId,
        prompt
      });
      console.log(requestId)
      // 실제 환경에서는 응답을 기다리지만, 여기서는 기본값 반환
      const result = {
        content: '내용량 정보 없음',
        material: '재질 정보 없음',
        type: '상품 유형 정보 없음',
      };
      return result
    } catch (error) {
      console.error('AI 한글 표시사항 생성 중 오류:', error);
      return {};
    }
  },
  /**
   * HTML로 이미지 만들기
   * @param {Object} labels
   * @param {Array<{id: string, label: string}>} [labelStructure] - 사용자 항목 구조(id→항목명)
   * @returns {String}
   */
  async generateLabelImage(labels, labelStructure) {
    // 라벨 이름 매핑 — 사용자가 고친 항목명(labelStructure) 우선
    const labelNameMap = buildLabelNameMap(labelStructure);

    // imageService의 generateHtmlToImage 함수 활용
    return await imageService.generateHtmlToImage({
      labels,
      labelNameMap
    });
  },
  /**
   * 라벨 이미지 업로드
   * @param {Object} product
   * @param {String} imageBase64
   * @returns {Promise<String>}
   */
  async uploadLabelImage(product, imageBase64, filename = null) {
    if (!imageBase64) {
      throw new Error('이미지 데이터가 없습니다.');
    }
    // filename이 제공되지 않으면 기본값 생성
    const labelFilename = filename || `korean_label_${product.registrationId}_${Date.now()}.png`;
    return await imageService.uploadImageToS3(imageBase64, labelFilename, 'koreanLabel');
  },
  /**
   * 표시 항목 키 이름 변환
   * @param {string} key - 키
   * @returns {string} 변환된 이름
   */
  getLabelNameByKey(key) {
    return buildLabelNameMap()[key] || key;
  },

  /**
   * 옵션별 한글 표시사항 배치 생성
   * @param {Object} product - 상품 객체
   * @returns {Promise<Object>} 옵션별 한글 표시사항 결과
   */
  async generateAllOptionLabels(product) {
    try {
      console.log('🏷️ 옵션별 한글 표시사항 배치 생성 시작');

      // [BUNDLE_DEBUG] 번들링 정보 추적
      console.log('[BUNDLE_DEBUG] 한글표시사항 - product.pricing:', product.pricing);
      console.log('[BUNDLE_DEBUG] 한글표시사항 - product.options:', product.options?.map(opt => ({
        optionNumber: opt.optionNumber,
        bundleQuantity: opt.bundleQuantity,
        korName: opt.korName
      })));

      // 처리할 옵션들 수집
      const optionsToProcess = product.options || [];

      if (optionsToProcess.length === 0) {
        console.warn('⚠️ 처리할 옵션이 없습니다.');
        return {
          success: false,
          error: '처리할 옵션이 없습니다.'
        };
      }

      // 모든 옵션 정보 준비
      const allOptions = optionsToProcess.map(option => ({
        optionNumber: option.optionNumber,
        korName: option.korName || option.optionNumber,
        bundleQuantity: option.bundleQuantity || 1 // [BUNDLE_DEBUG] 번들 수량 추가
      }));

      // 옵션별 한글 표시사항 배치 생성 요청
      const batchRequest = {
        workType: 'QUOTATION',
        task: 'GET_ALL_KOREAN_LABELS',
        productId: product.registrationId,
        requestArr: [{
          prompt: this.promptForAllOptionLabels(product, allOptions)
        }]
      };

      console.log('🤖 옵션별 한글 표시사항 배치 생성 요청:', {
        workType: batchRequest.workType,
        task: batchRequest.task,
        productId: batchRequest.productId,
        optionCount: allOptions.length
      });

      const result = await workflowService.requestExtensionWithPromise(
        batchRequest,
        {
          timeout: 120000,
          maxRetries: 2
        }
      );

      if (result.error) {
        throw new Error(result.error);
      }

      // 결과 검증 및 정리
      const validatedResults = this.validateBatchLabelResults(result, optionsToProcess);

      // 견적서에 저장한 고시정보(소재·구성)를 AI 값보다 먼저 쓴다
      optionsToProcess.forEach((option, optionIndex) => {
        const stored = validatedResults.results[option.optionNumber];
        if (stored && stored.labels) {
          stored.labels = this.applySavedNotices(stored.labels, product, optionIndex, option.bundleQuantity);
        }
      });

      console.log('✅ 옵션별 한글 표시사항 배치 생성 완료:', {
        totalOptions: optionsToProcess.length,
        successfulOptions: Object.keys(validatedResults.results).length,
        errors: validatedResults.errors.length
      });

      return validatedResults;

    } catch (error) {
      console.error('❌ 옵션별 한글 표시사항 배치 생성 중 오류:', error);
      return {
        success: false,
        error: error.message,
        results: {},
        errors: [{ error: error.message }]
      };
    }
  },

  /**
   * 라벨 칸 ← 견적서 고시 항목 이름 대응. 카테고리마다 고시 항목 이름이 달라 후보를 순서대로 본다.
   * 치수는 내용량으로 쓰지 않는다(옵션명 치수가 그대로 내용량에 찍히던 게 이번 버그).
   */
  NOTICE_NAMES_FOR_LABEL: {
    material: ['제품 소재', '재질', '소재', '주요 소재', '원료명 및 성분명', '원재료명 및 함량', '원재료명', '화장품법에 따라 기재, 표시하여야 하는 모든 성분'],
    content: ['제품구성', '제품 구성', '구성품', '내용량', '용량 또는 중량', '용량(중량)', '중량 및 용량']
  },

  hasHanChars(value) {
    return /[㐀-䶿一-鿿]/.test(String(value || ''));
  },

  /**
   * 사용자가 견적서에 저장한 그 옵션의 고시 값. 저장 위치 = coupangDoc.jsonDocument[i].legalPage.notices
   * (견적서 편집 화면이 여기에 쓴다. quotation.options[i].notices 는 여기서 뽑은 사본이라 옛 값일 수 있다).
   */
  savedNoticeLabelValues(product, optionIndex) {
    const notices = product?.coupangDoc?.jsonDocument?.[optionIndex]?.legalPage?.notices;
    const values = {};
    if (!Array.isArray(notices)) return values;
    Object.entries(this.NOTICE_NAMES_FOR_LABEL).forEach(([field, names]) => {
      for (const name of names) {
        const notice = notices.find(n => String(n?.noticeItemName || '').trim() === name);
        // 견적서 메모 꼬리("… (공급처 표기)")는 떼고 본다
        const value = String(notice?.noticeItemValue || '').trim()
          .replace(/\s*\([^)]*(공급처|표기|메모|견적)[^)]*\)\s*$/, '').trim();
        // 자리표시 값(해당사항없음·상세설명 참조·상세페이지 확인 …)은 고시 값으로 치지 않는다
        if (value && !/해당\s*(사항)?\s*없음|상세.*(참조|확인)/.test(value) && !this.hasHanChars(value)) {
          values[field] = value;
          break;
        }
      }
    });
    return values;
  },

  /**
   * AI 라벨 값을 마무리한다: ① 한자가 남은 값은 쓰지 않는다 ② 견적서 고시 값이 있으면 그 값이 우선.
   * 내용량을 바꿨으면 묶음 수량을 다시 붙인다 — 묶음 옵션 서류는 원본 고시(1개 기준)를 그대로 복사한다.
   * 바꾸지 않은 AI 내용량은 호출부에서 이미 묶음이 반영돼 있어 다시 붙이지 않는다(이중 반영 방지).
   */
  applySavedNotices(labels, product, optionIndex, bundleQuantity) {
    const result = { ...labels };
    const contentBefore = result.content;
    ['content', 'material', 'type'].forEach(field => {
      if (this.hasHanChars(result[field])) {
        console.warn(`한글표시사항: AI 값에 한자가 남아 버림 (${field}) - ${result[field]}`);
        result[field] = '상세정보참조';
      }
    });
    // 재질은 번역된 1688 속성 '재질' 값이 있으면 그걸로 (AI 가 덧붙인 짐작 소재 없이)
    if (result.material === '상세정보참조' && Array.isArray(product?.attributes)) {
      const attr = product.attributes.find(a => a?.attributeName === '재질');
      const attrValue = String(attr?.value || '').trim();
      if (attrValue && attrValue !== '기타' && !this.hasHanChars(attrValue)) result.material = attrValue;
    }
    const saved = this.savedNoticeLabelValues(product, optionIndex);
    if (saved.material) result.material = saved.material;
    if (saved.content) result.content = saved.content;
    if (result.content !== contentBefore) {
      // 고시값엔 개수·양 단위가 없을 수 있다 — main 의 내용량 단위 보장을 같이 건다(koreanLabelFields.ensureContentQuantity)
      result.content = ensureContentQuantity(this.contentForBundle(result.content, bundleQuantity), bundleQuantity);
    }
    return result;
  },

  /**
   * 모든 옵션에 대한 한글 표시사항 프롬프트 생성
   * @param {Object} product - 상품 객체
   * @param {Array} allOptions - 모든 옵션 정보
   * @returns {string} 생성된 프롬프트
   */
  promptForAllOptionLabels(product, allOptions) {
    // SEO된 상품명이 있다면 사용, 없다면 기본 상품명 사용
    const productName = product.seo?.productName || product.productName;
    const attributesInfo = product.attributes ? JSON.stringify(product.attributes) : '상품 상세 정보 참조';

    return `You are a Korean product labeling specialist. Generate Korean labeling information for ALL options of this product.

## Product Information
- Product Name: ${productName}
- Product Attributes: ${attributesInfo}

## 🎯 All Options to Process
${allOptions.map((option, idx) => `${idx+1}. Option Number: ${option.optionNumber}, Option Name: "${option.korName}"`).join('\n')}

## Required Information for Each Option
Generate the following 3 pieces of information for EACH option, considering how each option might differ:

1. **Content (내용량)**: How many pieces (or how much volume/weight) the package contains — NOT the product dimensions
   - Must include a count or amount unit. Examples: "1개", "2개입", "10매", "1세트", "520g", "100ml"
   - Do NOT copy size/dimension attributes (e.g. "52*20*0.8mm") as the content. If size matters, append it after the count: "1개 (크기 52*20*0.8mm)"
   - Different options may have different counts/weights
   - If unknown, use "1개"

2. **Material (원료명 및 성분명/재질)**: Raw materials or material composition
   - Examples: "면 100%", "폴리에스터 65%, 면 35%", "플라스틱, 금속", "천연 가죽"
   - If no specific information: "상세정보참조"

3. **Type (상품 유형)**: Product category/type
   - Examples: "생활용품", "주방잡화", "주방용품", "의류", "가전제품"
   - Usually same for all options, but may differ if options represent different product types
   - If no specific information: "상세정보참조"

## Important Guidelines
- Consider how each option name might indicate different content, materials, or types
- Size-related options (9cm, 12cm, etc.) should have different content values
${LABEL_EVIDENCE_RULES}
- Option names may end with a bundle suffix like "(2개묶음)", and the Product Name above may carry the same wording in its tail (e.g. "…정리대, 1단 2개묶음"). IGNORE bundle wording wherever it appears and describe ONE single unit — the bundle quantity is applied afterwards by the system, so including it here would double-count
- Use Korean language only. NEVER output Chinese characters (Hanzi) — translate Chinese attribute values into Korean
- Do NOT add materials that are not stated in the Product Attributes (e.g. do not append "기타 합성섬유" by guess)

## Response Format (ALL options in one response)
\`\`\`json
{
${allOptions.map(option => `  "${option.optionNumber}": {
    "content": "...",
    "material": "...",
    "type": "..."
  }`).join(',\n')}
}
\`\`\`

Generate appropriate Korean labeling information for each option based on the option names and product information.`;
  },

  /**
   * 배치 한글 표시사항 결과 검증
   * @param {Object} result - AI 배치 응답 결과
   * @param {Array} optionsToProcess - 처리할 옵션 목록
   * @returns {Object} 검증된 배치 결과
   */
  validateBatchLabelResults(result, optionsToProcess) {
    try {
      // AI 응답에서 JSON 파싱 (batchResults 구조 처리)
      let batchResults;

      if (result.batchResults && result.batchResults.length > 0) {
        const batchResult = result.batchResults[0];
        if (batchResult.result) {
          batchResults = batchResult.result;
        } else if (batchResult.processedText) {
          // JSON 블록 제거 후 파싱
          let jsonText = batchResult.processedText;
          jsonText = jsonText.replace(/```json\n|\n```/g, '');
          jsonText = jsonText.replace(/```\n|\n```/g, '');
          jsonText = jsonText.trim();
          batchResults = JSON.parse(jsonText);
        }
      } else if (typeof result === 'string') {
        batchResults = JSON.parse(result);
      } else {
        batchResults = result;
      }

      console.log('🔍 파싱된 한글 표시사항 배치 결과:', Object.keys(batchResults || {}));

      const validatedResults = {
        success: true,
        results: {},
        errors: []
      };

      // 각 옵션별로 결과 검증
      optionsToProcess.forEach(option => {
        const optionNumber = option.optionNumber;
        const optionResult = batchResults[optionNumber];

        if (optionResult) {
          try {
            // 개별 옵션 결과 검증
            const validatedLabels = this.validateLabelResponse(optionResult);

            validatedResults.results[optionNumber] = {
              success: true,
              labels: validatedLabels
            };

            console.log(`✅ 옵션 ${optionNumber} 한글 표시사항 검증 완료`);

          } catch (error) {
            console.error(`❌ 옵션 ${optionNumber} 한글 표시사항 검증 실패:`, error);
            validatedResults.errors.push({
              optionNumber: optionNumber,
              error: error.message
            });

            // 실패한 옵션도 기본값으로 설정
            validatedResults.results[optionNumber] = {
              success: false,
              error: error.message,
              labels: {
                content: '상세정보참조',
                material: '상세정보참조',
                type: '상세정보참조'
              }
            };
          }
        } else {
          console.warn(`⚠️ 옵션 ${optionNumber}에 대한 한글 표시사항 결과가 없습니다.`);
          validatedResults.errors.push({
            optionNumber: optionNumber,
            error: '결과 없음'
          });

          // 결과가 없는 옵션도 기본값으로 설정
          validatedResults.results[optionNumber] = {
            success: false,
            error: '결과 없음',
            labels: {
              content: '상세정보참조',
              material: '상세정보참조',
              type: '상세정보참조'
            }
          };
        }

        // 묶음 옵션이면 내용량에 수량을 반영한다 (성공·실패 분기 공통)
        const storedResult = validatedResults.results[optionNumber];
        if (storedResult && storedResult.labels) {
          storedResult.labels.content = ensureContentQuantity(
            this.contentForBundle(storedResult.labels.content, option.bundleQuantity), option.bundleQuantity);
        }
      });

      // 전체 성공 여부 결정
      validatedResults.success = validatedResults.errors.length === 0;

      console.log('✅ 배치 한글 표시사항 결과 검증 완료:', {
        totalOptions: optionsToProcess.length,
        successfulOptions: Object.keys(validatedResults.results).filter(key =>
          validatedResults.results[key].success
        ).length,
        failedOptions: validatedResults.errors.length
      });

      return validatedResults;

    } catch (error) {
      console.error('배치 한글 표시사항 결과 검증 중 오류:', error);

      // 전체 실패 시 모든 옵션에 기본값 설정
      const fallbackResults = {
        success: false,
        results: {},
        errors: [{ error: error.message }]
      };

      optionsToProcess.forEach(option => {
        fallbackResults.results[option.optionNumber] = {
          success: false,
          error: error.message,
          labels: {
            // 묶음 옵션이면 최소한 개수는 맞춘다
            content: ensureContentQuantity(this.contentForBundle('상세정보참조', option.bundleQuantity), option.bundleQuantity),
            material: '상세정보참조',
            type: '상세정보참조'
          }
        };
      });

      return fallbackResults;
    }
  },

  /**
   * 개별 한글 표시사항 응답 검증
   * @param {Object} labelResult - AI 응답 결과
   * @returns {Object} 검증된 한글 표시사항
   */
  validateLabelResponse(labelResult) {
    const validatedLabels = {
      content: labelResult.content || '상세정보참조',
      material: labelResult.material || '상세정보참조',
      type: labelResult.type || '상세정보참조'
    };

    // 빈 문자열 체크
    if (!validatedLabels.content.trim()) {
      validatedLabels.content = '상세정보참조';
    }
    if (!validatedLabels.material.trim()) {
      validatedLabels.material = '상세정보참조';
    }
    if (!validatedLabels.type.trim()) {
      validatedLabels.type = '상세정보참조';
    }

    return validatedLabels;
  },

  /**
   * 묶음 수량을 내용량에 반영한다.
   * 수량은 계산이지 창작이 아니라서 AI 응답에 맡기지 않고 코드로 붙인다
   * (실제로 "블랙 (2개묶음)" 옵션에 AI 가 "1개" 를 돌려준 사례가 있다).
   * @param {String} content - AI 가 만든 1개 기준 내용량
   * @param {Number} bundleQuantity - 묶음 수량
   * @returns {String} 묶음 수량이 반영된 내용량
   */
  contentForBundle(content, bundleQuantity) {
    const quantity = Number(bundleQuantity) || 1;

    // 묶음이 아니면 기존 값을 그대로 돌려준다 (기존 상품 표시사항이 달라지면 안 된다)
    if (quantity <= 1) {
      return content;
    }

    const base = String(content || '').trim();

    // 내용량을 모르는 경우엔 개수만 적는다 ("상세정보참조 x 2개" 는 말이 안 된다)
    if (!base || base === '상세정보참조') {
      return `${quantity}개`;
    }

    // 내용에 이미 묶음이 적혀 있으면 또 붙이지 않는다. 판정은 "묶음" 이라는 낱말 하나로만 한다 —
    // 개당 내용량("120g", "30cm x 20cm", "1개", "500ml")에는 이 낱말이 들어가지 않고,
    // 묶음을 뜻하는 표현에는 반드시 들어간다. 실측(260919001001_3, bq 2)에서 AI 가 상품명
    // 꼬리("…정리대, 1단 2개묶음")를 보고 "1개 (2개 묶음)" 을 돌려줘, 거기에 "x 2개" 가 또 붙었다.
    // "N개"·"N세트" 처럼 넓은 패턴으로 잡으면 개당 내용량("1개")을 묶음으로 오인해 수량이 아예
    // 안 붙는다 — 2개를 보내면서 "1개" 로 적히는 쪽이 더 나쁜 표기다.
    // 숫자는 세는 단위(개/쌍/세트…) 뒤에서만 인정한다. "20cm 묶음" 의 20 을 묶음 수량으로 잡으면
    // 치수를 고쳐버린다.
    const writtenBundle = base.match(/(\d+)\s*(?:개입|개|쌍|매|장|세트|팩|입|P|p)?\s*묶음/);
    if (writtenBundle) {
      const writtenQuantity = Number(writtenBundle[1]);
      if (writtenQuantity === quantity) {
        return base;
      }

      // 수량이 어긋나면 bundleQuantity 를 믿는다. 그게 실제로 보내는 개수이고(가격·재고·옵션명이
      // 다 그 값으로 만들어진다), 내용에 적힌 숫자는 AI 가 상품명에서 주워온 짐작이다.
      // 표시사항이라 조용한 불일치가 제일 나쁘니 로그를 남기고 옵션 수량으로 맞춘다.
      console.warn(`한글표시사항 내용량: 적힌 묶음 수량(${writtenQuantity})이 옵션 수량(${quantity})과 달라 옵션 수량으로 맞춘다 - ${base}`);
      return base.replace(writtenBundle[0], writtenBundle[0].replace(String(writtenQuantity), String(quantity)));
    }

    // "1개", "30cm 1개" 처럼 끝이 "1<단위>" 면 그 1 을 묶음 수량으로 바꾼다
    const singleUnit = base.match(/1\s*(개|쌍|매|장|세트|팩|켤레|병|봉|박스|캔|포|족|권|롤|자루)\s*$/);
    if (singleUnit) {
      return `${base.slice(0, singleUnit.index)}${quantity}${singleUnit[1]}`;
    }

    // 무게·부피·치수는 곱해서 적을 수 없으니 개당 값에 묶음 수를 덧붙인다
    return `${base} x ${quantity}개`;
  },

  /**
   * 라벨 이미지에 찍을 제품명을 만든다. 묶음 판정은 **옵션 단위**다.
   *
   * 상품 단위 플래그(pricing.bundleInfo.hasBundling)로 판정하면 안 된다. 한 상품 안에서
   * 옵션마다 묶음 수량이 다르다 — 1개짜리 옵션과 2개묶음 옵션이 같이 있다. 상품 플래그로
   * 붙이면 **묶음이 아닌 옵션의 라벨에도 묶음 표기가 번진다.** 그 플래그는 묶음이 아닌
   * 경로(옵션 하나만 묶는 경우)에서는 아예 채워지지도 않아서, 켜는 방향으로 맞추는 것도 틀린다.
   * 내용량(contentForBundle)이 이미 옵션 단위로 판정하니 같은 기준을 쓴다.
   *
   * 판별·접미 규칙은 옵션명을 만드는 것과 같은 bundleOptionService 하나만 쓴다.
   * withBundleSuffix 가 기존 "(N개묶음)" 을 먼저 떼고 붙이므로 표기가 두 번 붙지 않는다.
   *
   * @param {Object} product - 상품 객체
   * @param {Object} option - 이 라벨이 붙는 옵션 (없으면 묶음 표기 없음)
   * @returns {String} 라벨에 찍을 제품명
   */
  labelProductNameFor(product, option) {
    const baseName = product?.seo?.productName || product?.productNameTrans || '';
    const bundle = bundleOptionService.bundleLabelInfo(option);

    // 묶음이 아니면 기존 동작 그대로 (제품명을 건드리지 않는다)
    if (!bundle) return baseName;

    // 상품명에 이미 묶음 수량이 적혀 있으면 그대로 둔다.
    // SEO 가 만든 상품명은 옵션 요약을 꼬리에 달고 온다 — 실측(260919001001_3):
    // "…정리대, 1단 2개묶음". 괄호가 없어서 접미 규칙으로는 떼이지 않으니,
    // 그 위에 또 붙이면 "2개묶음 (2개묶음)" 이 된다. 표시사항은 규제 대상이라 중복을 두면 안 된다.
    // 상품 전체 묶음 수가 N 이면 상품명 속 «N세트·N개 세트·N개» 도 묶음 표기로 본다(윈 3452 PR261021-56DBDD) — 잣대는 bundleOptionService.productNameHasBundle 하나
    const stripped = bundleOptionService.stripBundleSuffix(baseName);
    if (stripped.includes(`${bundle.bundleQuantity}개묶음`) || bundleOptionService.productNameHasBundle(stripped, product, bundle.bundleQuantity)) {
      console.log(`한글표시사항 제품명: 묶음 표기가 이미 있어 그대로 사용 - ${baseName}`);
      return baseName;
    }

    const labelProductName = bundleOptionService.withBundleSuffix(baseName, bundle.bundleQuantity);
    console.log(`한글표시사항 제품명: 묶음 표기 반영 - ${labelProductName}`);
    return labelProductName;
  },

  /**
   * 한글 표시사항 프롬프트 (기존 단일 옵션용 - 호환성 유지)
   * @param {Object} product - 상품 객체
   * @returns {string} 생성된 프롬프트
   */
  promptForKoreanLabels(product) {
    // SEO된 상품명이 있다면 사용, 없다면 기본 상품명 사용
    let productName = product.seo?.productName || product.productName;

    // 번들 정보 확인 및 상품명에 추가
    if (product.pricing && product.pricing.bundleInfo && product.pricing.bundleInfo.hasBundling) {
      const bundleText = product.pricing.bundleInfo.bundleText;
      productName = `${productName} (${bundleText})`;
      console.log(`한글표시사항 프롬프트: 번들 정보 포함 - ${productName}`);
    }

    const attributesInfo = product.attributes ? JSON.stringify(product.attributes) : '상품 상세 정보 참조';

    // 번들 정보가 있는 경우 프롬프트에 추가 안내
    const bundleNote = product.pricing?.bundleInfo?.hasBundling
      ? `\n    ⚠️ 중요: 이 상품은 ${product.pricing.bundleInfo.bundleText}으로 판매됩니다. 내용량 생성 시 이를 반드시 반영하세요.`
      : '';

    return `다음은 "${productName}" 상품의 정보입니다. 아래 상품 정보를 참고해서, 한국어로 3가지 정보를 생성해주세요.

    상품명: ${productName}
    상품 속성: ${attributesInfo}${bundleNote}

    1.  내용량: 한 포장에 든 개수(또는 무게·부피)를 표시합니다. 크기(치수)가 아닙니다. 반드시 개수·양 단위를 넣으세요. 예를 들어, "1개", "2개입", "10매", "1세트", "520g", "100ml" 와 같은 형식으로 작성하세요. 상품 속성의 크기(예: "52*20*0.8mm")를 내용량으로 옮기지 마세요 — 크기를 꼭 적어야 하면 개수 뒤에 "1개 (크기 52*20*0.8mm)" 처럼 붙이세요. 개수를 알 수 없으면 "1개" 라고 입력하세요.
    2.  원료명 및 성분명(재질): 해당 상품의 원료 또는 재질에 대한 정보를 표시합니다. 예를 들어, "면 100%", "폴리에스터 65%, 면 35%", "플라스틱, 금속", "천연 가죽" 와 같은 형식으로 작성하세요. 재질에 대한 정보가 없다면 "상세정보참조" 라고 입력하세요.
    3.  상품 유형: 해당 상품의 유형을 표시합니다. 예를 들어, "생활용품", "주방잡화", "주방용품", "의류", "가전제품" 와 같은 형식으로 작성하세요. 상품 유형에 대한 정보가 없다면 "상세정보참조" 라고 입력하세요.

    모든 값은 한글로만 작성하세요(한자·중국어 금지, 중국어 속성값은 한국어로 번역). 상품 속성에 없는 재질을 짐작해 덧붙이지 마세요.

    응답은 반드시 아래 JSON 형식으로 작성해주세요:
    {
        "content": "내용량",
        "material": "원료명 및 성분명(재질)",
        "type": "상품 유형"
    }`;
  },

  /**
   * 옵션별 한글 표시사항 배치 결과 적용
   * @param {Object} product - 상품 객체
   * @param {Object} batchResults - 배치 결과
   * @returns {Promise<boolean>} 적용 성공 여부
   */
  async applyBatchLabelResults(product, batchResults) {
    try {
      console.log('🔧 옵션별 한글 표시사항 결과 적용 시작');

      const updateFields = {
        'koreanLabelStatus': 'completed',
        'updatedAt': Date.now()
      };

      // 각 옵션별로 개별 이미지 생성 및 적용
      // 옵션 한 장에 4~6초 — 옵션 50개면 4분을 넘는다. 몇 번째 옵션인지 남긴다(진행 문구·목록 칸 「12/50」).
      // 대표이미지(mainImageService)와 같은 기준 — 옵션 하나를 끝낸 뒤에 센다(마지막에 N/N).
      const labelEntries = Object.entries(batchResults);
      let labelDone = 0;
      const countLabelDone = () => setStepCount(product.registrationId, 'koreanLabel', ++labelDone, labelEntries.length);
      setStepCount(product.registrationId, 'koreanLabel', 0, labelEntries.length);
      for (const [optionNumber, result] of labelEntries) {
        if (result.success && result.labels) {
          console.log(`🏷️ 옵션 ${optionNumber} 한글 표시사항 이미지 생성 중...`);

          // 해당 옵션 찾기
          const optionIndex = product.options.findIndex(opt => opt.optionNumber === optionNumber);
          if (optionIndex === -1) {
            console.warn(`⚠️ 옵션 ${optionNumber}을 찾을 수 없습니다.`);
            countLabelDone();
            continue;
          }

          // 라벨 정보 구성 (옵션별 개별 정보 사용)
          // 제품명의 묶음 표기도 옵션 단위로 판정한다 — 옵션마다 묶음 수량이 다르다.
          const labelProductName = this.labelProductNameFor(product, product.options[optionIndex]);

          const allLabels = {
            productName: labelProductName,
            manufacturer: product.manufacturer || store.state.settings[0]?.defaultSettings?.manufacturer || '',
            importer: store.state.settings[0]?.defaultSettings?.importer || '',
            madein: '중국',
            content: result.labels.content || '상세정보참조',
            material: result.labels.material || '상세정보참조',
            type: result.labels.type || '상세정보참조',
            caution: '용도 외에 사용금지. 파손및화기주의',
            standard: '14세이상'
          };

          // 라벨 이름 매핑
          const labelNameMap = buildLabelNameMap();

          // HTML에서 이미지 생성
          const imageBase64 = await imageService.generateHtmlToImage({
            labels: allLabels,
            labelNameMap
          });

          // S3에 업로드 (옵션별 개별 파일명)
          const filename = `korean_label_${product.registrationId}_${optionNumber}_${Date.now()}.png`;
          const saveResult = await imageService.uploadImageToS3(imageBase64, filename, 'koreanLabel');

          // 옵션별 한글 표시사항 정보 업데이트
          updateFields[`options.${optionIndex}.koreanLabels`] = result.labels;
          updateFields[`options.${optionIndex}.koreanLabelImage`] = saveResult.imageUrl;
          updateFields[`options.${optionIndex}.koreanLabelStatus`] = 'completed';

          console.log(`✅ 옵션 ${optionNumber} 한글 표시사항 이미지 생성 완료: ${saveResult.imageUrl}`);
        } else {
          console.warn(`⚠️ 옵션 ${optionNumber} 한글 표시사항 생성 실패, 기본값 적용`);

          const optionIndex = product.options.findIndex(opt => opt.optionNumber === optionNumber);
          if (optionIndex !== -1) {
            updateFields[`options.${optionIndex}.koreanLabels`] = {
              content: '상세정보참조',
              material: '상세정보참조',
              type: '상세정보참조'
            };
            updateFields[`options.${optionIndex}.koreanLabelStatus`] = 'completed';
          }
        }
        countLabelDone();
      }

      // 상품 레벨 한글 표시사항 정보도 업데이트 (첫 번째 옵션 기준)
      const firstOptionResult = Object.values(batchResults)[0];
      if (firstOptionResult && firstOptionResult.success && firstOptionResult.labels) {
        updateFields['koreanLabels'] = firstOptionResult.labels;

        // 상품 레벨 이미지도 첫 번째 옵션과 동일하게 설정
        const firstOption = product.options[0];
        if (firstOption) {
          const firstOptionIndex = 0;
          updateFields['koreanLabelImage'] = updateFields[`options.${firstOptionIndex}.koreanLabelImage`];
        }
      }

      // 🔧 coupangDoc 업데이트 추가 (기존 updateProductWithCoupangDocPartial 로직 적용)
      if (product.coupangDoc && product.coupangDoc.jsonDocument) {
        console.log('🔧 coupangDoc에 옵션별 한글 표시사항 이미지 업데이트 시작');

        // 각 옵션별로 coupangDoc의 해당 jsonDocument에 이미지 업데이트
        Object.entries(batchResults).forEach(([optionNumber, result]) => {
          if (result.success && result.labels) {
            // 해당 옵션의 인덱스 찾기
            const optionIndex = product.options.findIndex(opt => opt.optionNumber === optionNumber);
            if (optionIndex !== -1 && product.coupangDoc.jsonDocument[optionIndex]) {
              const docItem = product.coupangDoc.jsonDocument[optionIndex];

              // imagePage.images.labelImages 구조 확인 및 생성
              if (docItem.imagePage && docItem.imagePage.images) {
                // labelImages 배열이 없으면 생성
                if (!docItem.imagePage.images.labelImages) {
                  updateFields[`coupangDoc.jsonDocument.${optionIndex}.imagePage.images.labelImages`] = [];
                }

                // 한글 표시사항 라벨 이미지 업데이트
                const labelImageIndex = docItem.imagePage.images.labelImages ?
                  docItem.imagePage.images.labelImages.findIndex(
                    img => img.labelImageType === '제품 한글 표시사항 라벨 또는 도안 이미지'
                  ) : -1;

                // 해당 옵션의 이미지 URL 가져오기
                const imageUrl = updateFields[`options.${optionIndex}.koreanLabelImage`];

                if (imageUrl) {
                  if (labelImageIndex !== -1) {
                    // 기존 이미지 업데이트
                    updateFields[`coupangDoc.jsonDocument.${optionIndex}.imagePage.images.labelImages.${labelImageIndex}.labelImageFiles`] = imageUrl;
                  } else {
                    // 새 이미지 추가 - 전체 배열을 업데이트하는 방식으로 처리
                    const existingLabelImages = docItem.imagePage.images.labelImages || [];
                    const updatedLabelImages = [...existingLabelImages, {
                      labelImageType: '제품 한글 표시사항 라벨 또는 도안 이미지',
                      labelImageFiles: imageUrl
                    }];

                    updateFields[`coupangDoc.jsonDocument.${optionIndex}.imagePage.images.labelImages`] = updatedLabelImages;
                  }

                  console.log(`✅ 옵션 ${optionNumber} coupangDoc 이미지 업데이트: ${imageUrl.substring(0, 50)}...`);
                }
              }
            }
          }
        });

        console.log('✅ coupangDoc 옵션별 한글 표시사항 이미지 업데이트 완료');
      }

      // 서버에 부분 업데이트 요청
      await apiService.updateRocketProductPartial(product._id, updateFields);

      // store 상태 부분 업데이트
      store.commit('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });

      console.log('✅ 옵션별 한글 표시사항 결과 적용 완료');
      return true;

    } catch (error) {
      console.error('❌ 옵션별 한글 표시사항 결과 적용 중 오류:', error);
      throw error;
    }
  },

  /**
   * 기존 방식으로 폴백 처리 (모든 옵션에 동일한 한글 표시사항 적용)
   * @param {Object} product - 상품 객체
   * @returns {Promise<boolean>} 처리 성공 여부
   */
  async processFallbackLabels(product) {
    try {
      console.log('🔄 기존 방식으로 한글 표시사항 폴백 처리 시작');

      // 기존 단일 옵션 방식으로 AI 요청
      const prompt = this.promptForKoreanLabels(product);

      const result = await this.requestExtension({
        type: 'KOREAN_LABELS',
        productId: product.registrationId,
        prompt
      });

      console.log('한글 표시사항 폴백 AI 결과:', result);

      // 기존 handleResult 함수 사용 — false(파싱·그리기·저장 실패)면 아래 기본 데이터 길로
      if (!(await this.handleResult(product, result))) {
        throw new Error('한글 표시사항 결과 적용 실패');
      }

      console.log('✅ 한글 표시사항 폴백 처리 완료');
      return true;

    } catch (error) {
      console.error('❌ 한글 표시사항 폴백 처리 중 오류:', error);

      // 최종 폴백: 기본 데이터로 완료 처리
      const defaultLabels = {
        content: '상세정보참조',
        material: '상세정보참조',
        type: '상세정보참조'
      };

      // 기본 데이터도 못 넣으면(그리기 한도 등) 완료로 두지 않는다 — 작업개시가 'error' 로 남긴다
      if (!(await this.handleResult(product, defaultLabels))) {
        throw new Error(`한글 표시사항 기본값 적용 실패: ${error.message}`);
      }

      console.log('✅ 한글 표시사항 최종 폴백 처리 완료');
      return true;
    }
  },

  /**
   * 확장 프로그램에 요청 전송 - workflowService.requestExtensionWithPromise 사용
   * @param {Object} requestData - 요청 데이터
   * @returns {Promise<Object>} 한글 표시사항 생성 결과
   */
  async requestExtension(requestData) {
    try {
      console.log('한글 표시사항 확장 요청 시작:', requestData);

      // workflowService.requestExtensionWithPromise 사용
      const result = await workflowService.requestExtensionWithPromise(
        {
          workType: requestData.type,
          productId: requestData.productId,
          requestArr: requestData.requestArr || (requestData.prompt ? [{ prompt: requestData.prompt }] : [])
        },
        {
          timeout: 45000, // 45초 타임아웃
          maxRetries: 2   // 최대 2회 재시도
        }
      );

      console.log('한글 표시사항 확장 응답 수신:', result);
      return result;
    } catch (error) {
      console.error('한글 표시사항 확장 요청 실패:', error);
      throw error;
    }
  },
};

export default koreanLabelService;